/**
 * Card component for displaying and validating a single fact
 *
 * Legal compliance: Uses opinion-framed language per defamation risk mitigation.
 * Displays graduated assessment labels rather than binary true/false.
 */

import { useState } from 'react'
import type { FactResult, HumanValidationInput, SearchSourcesResult } from '../types'
import { searchApi } from '../services/api'
import { ProvideSourceModal } from './ProvideSourceModal'
import { SourceSnapshotModal } from './SourceSnapshotModal'
import { ChipRow, SCOPE_AMBER, secondaryChipKeys } from './factChips'

// Type definitions for classification and confidence levels
type Classification = 'SUPPORTS' | 'REFUTES' | 'NOT_ENOUGH_INFO'
type ConfidenceLevel = 'high' | 'medium' | 'low' | 'very_low' | 'unknown'

// Score thresholds for graduated label mapping
const SCORE_THRESHOLD_HIGH = 0.8
const SCORE_THRESHOLD_MEDIUM = 0.6

// Default result for missing/undefined values
const NOT_ENOUGH_INFO_RESULT = { label: 'Not Enough Info', color: '#95a5a6' }
// Supported only as far as it goes (scope claim_broader or claim_narrower).

// Plain-language reason for a NOT_ENOUGH_INFO verdict (nei_reason).
const NEI_REASON_TEXT: Record<string, string> = {
  not_mentioned: "The source doesn't mention this.",
  different_entity:
    'The source covers a related but different item (another version, row or group).',
  undetermined_at_time: 'The source says this was not yet known when it was written.',
  partial: 'The source covers only part of the claim.',
}

/**
 * Get display text and color for AI classification (3-level)
 * Used for primary AI verdict display
 */
const getClassificationDisplay = (
  classification: Classification | undefined,
  failureMessage?: string,
  scope?: FactResult['scope']
): { label: string; color: string; icon: string } => {
  if (failureMessage) {
    return { label: failureMessage.toUpperCase(), color: '#f39c12', icon: '⚠️' }
  }
  switch (classification) {
    case 'SUPPORTS':
      // Supported only as far as it goes: amber, so it can't pass for a clean
      // SUPPORTED. The scope box below says what the claim leaves out or adds.
      if (scope === 'claim_broader') {
        return { label: 'SUPPORTED, MISSING CAVEATS', color: SCOPE_AMBER.fg, icon: '⚠️' }
      }
      if (scope === 'claim_narrower') {
        return { label: 'SUPPORTED, NARROWER SCOPE', color: SCOPE_AMBER.fg, icon: '⚠️' }
      }
      return { label: 'SUPPORTED', color: '#27ae60', icon: '✅' }
    case 'REFUTES':
      return { label: 'REFUTED', color: '#e74c3c', icon: '❌' }
    case 'NOT_ENOUGH_INFO':
    default:
      return { label: 'NOT ENOUGH INFO', color: '#95a5a6', icon: '❓' }
  }
}

/**
 * Maps 3-level classification + confidence to graduated display labels.
 * Per legal guidance: Use nuanced language rather than binary true/false.
 *
 * Mapping based on IFCN-recommended graduated scale:
 * - Accurate: High confidence SUPPORTS
 * - Mostly Accurate: Medium confidence SUPPORTS
 * - Needs Context: Low confidence SUPPORTS or partial match
 * - Misleading: Low confidence REFUTES (creates false impression)
 * - Inaccurate: Medium+ confidence REFUTES
 * - Insufficient Evidence: NOT_ENOUGH_INFO or missing data
 */
const getGraduatedLabel = (
  classification: Classification | undefined,
  confidence: ConfidenceLevel | undefined,
  score: number | undefined,
  scope?: FactResult['scope']
): { label: string; color: string } => {
  // Handle missing data
  if (!classification) {
    return NOT_ENOUGH_INFO_RESULT
  }
  // A supported claim that leaves out caveats, or adds detail the source
  // doesn't state, is not "Accurate" as worded.
  if (classification === 'SUPPORTS' && (scope === 'claim_broader' || scope === 'claim_narrower')) {
    return { label: 'Needs Context', color: SCOPE_AMBER.fg }
  }

  const safeScore = score ?? 0
  const safeConfidence = confidence ?? 'unknown'

  if (classification === 'SUPPORTS') {
    if (safeConfidence === 'high' || safeScore >= SCORE_THRESHOLD_HIGH) {
      return { label: 'Accurate', color: '#27ae60' }
    } else if (safeConfidence === 'medium' || safeScore >= SCORE_THRESHOLD_MEDIUM) {
      return { label: 'Mostly Accurate', color: '#2ecc71' }
    } else {
      return { label: 'Needs Context', color: '#f39c12' }
    }
  } else if (classification === 'REFUTES') {
    // Medium+ confidence REFUTES = Inaccurate, Low confidence = Misleading
    if (
      safeConfidence === 'high' ||
      safeConfidence === 'medium' ||
      safeScore >= SCORE_THRESHOLD_MEDIUM
    ) {
      return { label: 'Inaccurate', color: '#e74c3c' }
    } else {
      return { label: 'Misleading', color: '#e67e22' }
    }
  } else {
    // NOT_ENOUGH_INFO
    return NOT_ENOUGH_INFO_RESULT
  }
}

// Collapsible section component
const CollapsibleSection = ({
  title,
  children,
  defaultOpen = false,
}: {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  return (
    <div style={{ marginBottom: '1rem' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          fontSize: '1rem',
          fontWeight: 600,
          color: '#2c3e50',
        }}
      >
        <span
          style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'none' }}
        >
          ▶
        </span>
        {title}
      </button>
      {isOpen && <div style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>{children}</div>}
    </div>
  )
}

interface FactValidationCardProps {
  fact: FactResult
  resultId: string
  userEmail?: string
  onValidate: (validation: HumanValidationInput) => Promise<void>
  onSourceProvided?: () => void
  /** Result-level source URL, used when the fact carries no citation
   *  (older URL-failure records) so "Provide Source Content" still works */
  fallbackSourceUrl?: string
}

export const FactValidationCard = ({
  fact,
  resultId,
  userEmail,
  onValidate,
  onSourceProvided,
  fallbackSourceUrl,
}: FactValidationCardProps) => {
  const [isValidating, setIsValidating] = useState(false)
  const [notes, setNotes] = useState('')
  const [showValidation, setShowValidation] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [showProvideSource, setShowProvideSource] = useState(false)
  const [showSnapshot, setShowSnapshot] = useState(false)
  const [isReverifying, setIsReverifying] = useState(false)
  const [showSourceOptions, setShowSourceOptions] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<SearchSourcesResult | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)

  // Determine source content status
  const hasUserProvidedContent = fact.cached_source_status === 'user_provided'

  // URL-failure records (esp. older ones) carry no per-fact citation - fall
  // back to the result-level source URL so "Provide Source Content" still works
  const effectiveCitation = fact.citation || fallbackSourceUrl

  const handleValidate = async (isValid: boolean) => {
    if (!userEmail) return

    setIsValidating(true)
    try {
      await onValidate({
        fact_key: fact.fact_key,
        is_valid: isValid,
        human_notes: notes || undefined,
        validator_email: userEmail,
      })
      setShowValidation(false)
      setIsEditing(false)
      setNotes('')
    } catch (error) {
      console.error('Validation failed:', error)
    } finally {
      setIsValidating(false)
    }
  }

  const hasHumanValidation = !!fact.human_validation
  // Only result owners can submit validations (enforced server-side),
  // so if logged in and there's a validation, user can edit it
  const canEditValidation = hasHumanValidation && !!userEmail

  // Determine if human validation agrees with AI (confirmation) or disagrees (correction)
  const humanAgreesWithAI = hasHumanValidation && fact.human_validation!.is_valid === fact.is_valid
  const isHumanCorrection = hasHumanValidation && !humanAgreesWithAI

  // When human validation exists, use it as the primary status
  const primaryIsValid = hasHumanValidation ? fact.human_validation!.is_valid : fact.is_valid

  const handleReverify = async () => {
    setIsReverifying(true)
    try {
      const response = await fetch(`/api/v1/results/${resultId}/facts/${fact.fact_key}/reverify`, {
        method: 'POST',
        credentials: 'include',
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.detail || 'Failed to re-verify')
      }

      // Refresh the parent to get updated data
      if (onSourceProvided) {
        onSourceProvided()
      }
    } catch (error) {
      console.error('Re-verify failed:', error)
      alert(`Re-verify failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setIsReverifying(false)
    }
  }

  const handleSearchSources = async () => {
    setIsSearching(true)
    setSearchError(null)
    setSearchResults(null)
    try {
      const result = await searchApi.searchSourcesForFact(resultId, fact.fact_key)
      setSearchResults(result)
      // Refresh parent to show updated source_search_history
      if (onSourceProvided) {
        // Short delay so user sees the inline results before refresh
        setTimeout(() => onSourceProvided(), 1500)
      }
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'Search failed')
    } finally {
      setIsSearching(false)
    }
  }

  // Get AI classification display (3-level)
  const aiDisplay = getClassificationDisplay(fact.classification, fact.failure_message, fact.scope)
  const scopeCaveat =
    fact.classification === 'SUPPORTS' &&
    (fact.scope === 'claim_broader' || fact.scope === 'claim_narrower')
  const aiStatusColor = aiDisplay.color

  // Primary status color (human if available, else AI)
  const primaryStatusColor = hasHumanValidation
    ? fact.human_validation!.is_valid
      ? '#27ae60'
      : '#e74c3c'
    : aiStatusColor

  const confidenceColors: Record<string, string> = {
    high: '#27ae60',
    medium: '#f39c12',
    low: '#e67e22',
    very_low: '#e74c3c',
    unknown: '#95a5a6',
  }
  const confidenceColor = confidenceColors[fact.confidence_level] || '#95a5a6'

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '1rem',
        background: 'white',
      }}
    >
      {/* Fact Key */}
      <h3 style={{ marginTop: 0, color: '#2c3e50' }}>{fact.fact_key}</h3>

      {/* Fact Value */}
      <p style={{ fontSize: '1.1rem', fontWeight: 500 }}>{fact.value}</p>

      {/* Primary Verdict - Human validation if available, otherwise AI */}
      {hasHumanValidation && !isEditing ? (
        <div
          style={{
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            background: primaryIsValid ? '#e8f5e9' : '#ffebee',
            border: `2px solid ${primaryStatusColor}`,
          }}
        >
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
          >
            <div>
              <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>
                {primaryIsValid ? '✅' : '❌'}
              </span>
              <strong style={{ fontSize: '1.2rem', color: primaryStatusColor }}>
                {primaryIsValid ? 'ACCURATE' : 'INACCURATE'}
              </strong>
              <span
                style={{
                  marginLeft: '0.75rem',
                  padding: '0.2rem 0.5rem',
                  background: '#6c757d',
                  color: 'white',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                }}
              >
                HUMAN REVIEWED
              </span>
            </div>
            {canEditValidation && (
              <button
                onClick={() => {
                  setIsEditing(true)
                  setNotes(fact.human_validation?.notes || '')
                }}
                style={{
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Edit
              </button>
            )}
          </div>

          {/* Show if human corrected or confirmed AI */}
          <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#555' }}>
            {isHumanCorrection ? (
              <span>
                <strong style={{ color: '#e65100' }}>Corrected:</strong> AI analysis indicated{' '}
                {aiDisplay.label.toLowerCase()}
              </span>
            ) : (
              <span>
                <strong style={{ color: '#2e7d32' }}>Confirmed:</strong> Agrees with AI analysis
              </span>
            )}
          </div>

          {fact.human_validation!.validator_name && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.85rem', color: '#666' }}>
              Reviewed by: {fact.human_validation!.validator_name}
            </p>
          )}
          {fact.human_validation!.notes && (
            <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
              <strong>Notes:</strong> {fact.human_validation!.notes}
            </p>
          )}
          <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#7f8c8d' }}>
            {new Date(fact.human_validation!.validated_at).toLocaleString()}
          </p>
        </div>
      ) : (
        /* AI Validation as Primary (no human validation yet) */
        <div
          style={{
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            background:
              fact.failure_message || scopeCaveat
                ? '#fff8e1'
                : fact.classification === 'SUPPORTS'
                  ? '#e8f5e9'
                  : fact.classification === 'REFUTES'
                    ? '#ffebee'
                    : '#f5f5f5',
            border: `2px solid ${aiStatusColor}`,
          }}
        >
          <div>
            <span style={{ fontSize: '1.5rem', marginRight: '0.5rem' }}>{aiDisplay.icon}</span>
            <strong style={{ fontSize: '1.2rem', color: aiStatusColor }}>{aiDisplay.label}</strong>
            <span
              style={{
                marginLeft: '0.75rem',
                padding: '0.2rem 0.5rem',
                background: '#9e9e9e',
                color: 'white',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
              }}
            >
              AI ANALYSIS
            </span>
          </div>
        </div>
      )}

      {/* At-a-glance signal chips (secondary to the verdict above) */}
      <ChipRow chipKeys={secondaryChipKeys(fact)} style={{ marginBottom: '1rem' }} />

      {/* AI Details - shown as secondary when human reviewed, or expanded when AI is primary */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {hasHumanValidation && (
          <div>
            <strong>AI Analysis:</strong>{' '}
            <span style={{ color: aiStatusColor, fontWeight: 'bold' }}>{aiDisplay.label}</span>
          </div>
        )}
        <div>
          <strong>Assessment:</strong>{' '}
          <span
            style={{
              color: getGraduatedLabel(
                fact.classification,
                fact.confidence_level,
                fact.validation_score,
                fact.scope
              ).color,
              fontWeight: 'bold',
            }}
          >
            {
              getGraduatedLabel(
                fact.classification,
                fact.confidence_level,
                fact.validation_score,
                fact.scope
              ).label
            }
          </span>
        </div>
        <div>
          <strong>Confidence:</strong>{' '}
          <span style={{ color: confidenceColor, fontWeight: 'bold' }}>
            {fact.confidence_level.toUpperCase()}
          </span>
        </div>
        <div>
          <strong>Score:</strong> {(fact.validation_score * 100).toFixed(1)}%
        </div>
      </div>

      {/* Source URL / Citation metadata */}
      {fact.citation && (
        <div style={{ marginBottom: '1rem' }}>
          {fact.citation_metadata?.title ? (
            <div
              style={{
                background: '#f8f9fa',
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                border: '1px solid #e9ecef',
              }}
            >
              <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '0.25rem' }}>
                {fact.citation_metadata.title}
              </div>
              <div style={{ fontSize: '0.9rem', color: '#555', lineHeight: 1.5 }}>
                {fact.citation_metadata.authors && <span>{fact.citation_metadata.authors}</span>}
                {fact.citation_metadata.journal && (
                  <span>
                    {fact.citation_metadata.authors ? ' — ' : ''}
                    <em>{fact.citation_metadata.journal}</em>
                  </span>
                )}
                {fact.citation_metadata.year && <span> ({fact.citation_metadata.year})</span>}
                {fact.citation_metadata.volume && (
                  <span>
                    , {fact.citation_metadata.volume}
                    {fact.citation_metadata.issue && `(${fact.citation_metadata.issue})`}
                  </span>
                )}
                {fact.citation_metadata.pages && <span>: {fact.citation_metadata.pages}</span>}
              </div>
              {fact.citation_metadata.doi ? (
                <a
                  href={`https://doi.org/${fact.citation_metadata.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#3498db',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    display: 'inline-block',
                  }}
                >
                  DOI: {fact.citation_metadata.doi}
                </a>
              ) : (
                <a
                  href={fact.citation}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: '#3498db',
                    fontSize: '0.85rem',
                    marginTop: '0.25rem',
                    display: 'inline-block',
                    wordBreak: 'break-all',
                  }}
                >
                  {fact.citation}
                </a>
              )}
            </div>
          ) : (
            <div>
              <strong>Source:</strong>{' '}
              <a
                href={fact.citation}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#3498db', wordBreak: 'break-all' }}
              >
                {fact.citation}
              </a>
            </div>
          )}
        </div>
      )}

      {/* View the exact content this verdict was reached against (#84).
          Independent of the citation block: supplied-text facts have a
          snapshot but no citation URL. */}
      {fact.source_snapshot_id && resultId && (
        <div style={{ marginBottom: '1rem' }}>
          <button
            onClick={() => setShowSnapshot(true)}
            style={{
              background: 'none',
              border: '1px solid #cad6e2',
              borderRadius: '6px',
              padding: '0.35rem 0.75rem',
              color: '#4c6076',
              fontSize: '0.85rem',
              cursor: 'pointer',
            }}
          >
            📄 View source content used
          </button>
        </div>
      )}
      {showSnapshot && resultId && fact.fact_key && (
        <SourceSnapshotModal
          resultId={resultId}
          factKey={fact.fact_key}
          onClose={() => setShowSnapshot(false)}
        />
      )}

      {/* Scope: caveats the source states that the claim leaves out */}
      {(fact.scope === 'claim_broader' || fact.scope === 'claim_narrower') && (
        <div
          style={{
            background: SCOPE_AMBER.bg,
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: `1px solid ${SCOPE_AMBER.bd}`,
          }}
        >
          <strong style={{ color: SCOPE_AMBER.fg }}>
            ⚠{' '}
            {fact.scope === 'claim_broader'
              ? 'Caveats the claim leaves out'
              : "Details the claim adds that the source doesn't state"}
          </strong>
          {fact.scope_conditions && fact.scope_conditions.length > 0 && (
            <>
              <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#555', listStyle: 'disc' }}>
                {fact.scope_conditions.map((condition, i) => (
                  <li key={i}>{condition}</li>
                ))}
              </ul>
            </>
          )}
          {fact.classification !== 'REFUTES' && fact.corrected_claim && (
            <p style={{ marginTop: '0.5rem', marginBottom: 0, color: '#555' }}>
              <strong>As the source supports it:</strong> {fact.corrected_claim}
            </p>
          )}
        </div>
      )}

      {/* Why NOT_ENOUGH_INFO */}
      {fact.classification === 'NOT_ENOUGH_INFO' && fact.nei_reason && (
        <p style={{ margin: '0 0 1rem', color: '#57606a', fontSize: '0.9rem' }}>
          <strong>Why not enough info:</strong>{' '}
          {NEI_REASON_TEXT[fact.nei_reason] ?? fact.nei_reason}
        </p>
      )}

      {/* Corrected Claim - shown when REFUTES */}
      {fact.classification === 'REFUTES' && fact.corrected_claim && (
        <div
          style={{
            background: '#fff3e0',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #ffb74d',
          }}
        >
          <strong style={{ color: '#e65100' }}>📝 Suggested Correction:</strong>
          <p style={{ marginTop: '0.5rem', marginBottom: 0, color: '#555' }}>
            {fact.corrected_claim}
          </p>
        </div>
      )}

      {/* Content Quality Warning - show when source is incomplete OR fetch failed */}
      {(fact.content_appears_incomplete || fact.failure_message) && !hasUserProvidedContent && (
        <div
          style={{
            background: '#fff8e1',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #ffc107',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
          >
            <span>⚠️</span>
            <span style={{ color: '#856404' }}>
              <strong>{fact.failure_message ? 'Source Unavailable:' : 'Incomplete Source:'}</strong>{' '}
              {fact.failure_message ||
                fact.incomplete_content_reason ||
                'Source content may be incomplete'}
            </span>
          </div>
          {effectiveCitation && userEmail && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowProvideSource(true)}
                style={{
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Provide Source Content
              </button>
              <button
                onClick={handleReverify}
                disabled={isReverifying}
                style={{
                  background: isReverifying ? '#a569bd' : '#9b59b6',
                  color: 'white',
                  border: 'none',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '4px',
                  cursor: isReverifying ? 'wait' : 'pointer',
                  fontSize: '0.85rem',
                  opacity: isReverifying ? 0.7 : 1,
                }}
                title="Re-run verification with cached source content"
              >
                {isReverifying ? 'Verifying...' : 'Re-verify'}
              </button>
              <button
                onClick={handleSearchSources}
                disabled={isSearching}
                style={{
                  background: isSearching ? '#5fb878' : '#16a085',
                  color: 'white',
                  border: 'none',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '4px',
                  cursor: isSearching ? 'wait' : 'pointer',
                  fontSize: '0.85rem',
                  opacity: isSearching ? 0.7 : 1,
                }}
                title="Search the web for alternative sources that support this claim"
              >
                {isSearching ? 'Searching...' : 'Search for Sources'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Collapsible Source Options - available on all cards without existing warnings */}
      {effectiveCitation &&
        userEmail &&
        !hasUserProvidedContent &&
        !fact.content_appears_incomplete &&
        !fact.failure_message && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={() => setShowSourceOptions(!showSourceOptions)}
              style={{
                background: 'none',
                border: 'none',
                padding: '0.25rem 0',
                cursor: 'pointer',
                fontSize: '0.85rem',
                color: '#7f8c8d',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <span style={{ fontSize: '0.7rem' }}>{showSourceOptions ? '▾' : '▸'}</span>
              Source options
            </button>
            {showSourceOptions && (
              <div
                style={{
                  marginTop: '0.5rem',
                  padding: '0.75rem',
                  background: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef',
                }}
              >
                {fact.classification === 'NOT_ENOUGH_INFO' && (
                  <p
                    style={{
                      margin: '0 0 0.5rem 0',
                      fontSize: '0.85rem',
                      color: '#6c757d',
                    }}
                  >
                    The source may not contain enough information to verify this claim.
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowProvideSource(true)}
                    style={{
                      background: '#3498db',
                      color: 'white',
                      border: 'none',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    Provide Source Content
                  </button>
                  <button
                    onClick={handleReverify}
                    disabled={isReverifying}
                    style={{
                      background: isReverifying ? '#a569bd' : '#9b59b6',
                      color: 'white',
                      border: 'none',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '4px',
                      cursor: isReverifying ? 'wait' : 'pointer',
                      fontSize: '0.85rem',
                      opacity: isReverifying ? 0.7 : 1,
                    }}
                    title="Re-fetch and re-verify the source content"
                  >
                    {isReverifying ? 'Verifying...' : 'Re-fetch & Verify'}
                  </button>
                  <button
                    onClick={handleSearchSources}
                    disabled={isSearching}
                    style={{
                      background: isSearching ? '#5fb878' : '#16a085',
                      color: 'white',
                      border: 'none',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '4px',
                      cursor: isSearching ? 'wait' : 'pointer',
                      fontSize: '0.85rem',
                      opacity: isSearching ? 0.7 : 1,
                    }}
                    title="Search the web for alternative sources that support this claim"
                  >
                    {isSearching ? 'Searching...' : 'Search for Sources'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

      {/* User-provided content notice - show when content was manually provided */}
      {hasUserProvidedContent && fact.citation && (
        <div
          style={{
            background: '#e8f5e9',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            border: '1px solid #81c784',
          }}
        >
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}
          >
            <span>✅</span>
            <span style={{ color: '#2e7d32' }}>
              <strong>Source Content Provided</strong>
            </span>
          </div>
          {userEmail && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowProvideSource(true)}
                style={{
                  background: '#78909c',
                  color: 'white',
                  border: 'none',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Update Content
              </button>
              <button
                onClick={handleReverify}
                disabled={isReverifying}
                style={{
                  background: isReverifying ? '#a569bd' : '#9b59b6',
                  color: 'white',
                  border: 'none',
                  padding: '0.4rem 0.8rem',
                  borderRadius: '4px',
                  cursor: isReverifying ? 'wait' : 'pointer',
                  fontSize: '0.85rem',
                  opacity: isReverifying ? 0.7 : 1,
                }}
                title="Re-run verification with cached source content"
              >
                {isReverifying ? 'Verifying...' : 'Re-verify'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Search Results - inline display */}
      {searchError && (
        <div
          style={{
            background: '#fadbd8',
            padding: '0.75rem 1rem',
            borderRadius: '4px',
            marginBottom: '1rem',
            color: '#e74c3c',
          }}
        >
          Search failed: {searchError}
        </div>
      )}
      {searchResults && (
        <div
          style={{
            background: '#f0f9f4',
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            border: '1px solid #a3d9b1',
          }}
        >
          <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#16a085' }}>
            Alternative Source Search Results
          </h4>
          <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }}>
            {searchResults.search_summary}
          </p>

          {/* Auto-verification result */}
          {searchResults.verification_result && (
            <div
              style={{
                background:
                  searchResults.verification_result.classification === 'SUPPORTS'
                    ? '#e8f5e9'
                    : searchResults.verification_result.classification === 'REFUTES'
                      ? '#ffebee'
                      : '#f5f5f5',
                padding: '0.75rem',
                borderRadius: '4px',
                marginBottom: '0.75rem',
                border: `1px solid ${
                  searchResults.verification_result.classification === 'SUPPORTS'
                    ? '#81c784'
                    : searchResults.verification_result.classification === 'REFUTES'
                      ? '#e57373'
                      : '#bdbdbd'
                }`,
              }}
            >
              <strong>Auto-verification:</strong>{' '}
              <span
                style={{
                  color:
                    searchResults.verification_result.classification === 'SUPPORTS'
                      ? '#27ae60'
                      : searchResults.verification_result.classification === 'REFUTES'
                        ? '#e74c3c'
                        : '#95a5a6',
                  fontWeight: 'bold',
                }}
              >
                {searchResults.verification_result.classification}
              </span>
              <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                ({(searchResults.verification_result.validation_score * 100).toFixed(0)}%
                confidence)
              </span>
              {searchResults.display_id && (
                <a
                  href={`/results/${searchResults.display_id}`}
                  style={{
                    marginLeft: '0.75rem',
                    color: '#3498db',
                    fontSize: '0.85rem',
                  }}
                >
                  View full result
                </a>
              )}
            </div>
          )}

          {/* Candidate sources list */}
          {searchResults.candidate_sources.length > 0 && (
            <div>
              <strong style={{ fontSize: '0.9rem' }}>
                Candidate Sources ({searchResults.candidate_sources.length}):
              </strong>
              {searchResults.candidate_sources.map((source, idx) => (
                <div
                  key={idx}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'white',
                    borderRadius: '4px',
                    border: source.verified ? '2px solid #27ae60' : '1px solid #e0e0e0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3498db', fontWeight: 500, fontSize: '0.9rem' }}
                    >
                      {source.title}
                    </a>
                    {source.is_primary_source && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          padding: '1px 6px',
                          background: '#2ecc71',
                          color: 'white',
                          borderRadius: '3px',
                          fontWeight: 600,
                        }}
                      >
                        PRIMARY
                      </span>
                    )}
                    {source.published_date && (
                      <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        {source.published_date}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#555' }}>
                    {source.snippet}
                  </p>
                  {source.relevance_note && (
                    <p
                      style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.8rem',
                        color: '#7f8c8d',
                        fontStyle: 'italic',
                      }}
                    >
                      {source.relevance_note}
                    </p>
                  )}
                  {source.cited_primary_source_description && (
                    <p
                      style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.8rem',
                        color: '#8e44ad',
                      }}
                    >
                      Cites: {source.cited_primary_source_description}
                      {source.cited_primary_source_url && (
                        <>
                          {' '}
                          <a
                            href={source.cited_primary_source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3498db' }}
                          >
                            (link)
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Persisted source search context (shown when no inline results active) */}
      {!searchResults &&
        fact.source_search_candidates &&
        fact.source_search_candidates.length > 0 &&
        (() => {
          const verifiedSource = fact.source_search_candidates?.find((s) => s.verified)
          const hasSupport = fact.classification === 'SUPPORTS' && verifiedSource
          return (
            <div
              style={{
                background: hasSupport ? '#e8f5e9' : '#f0f9f4',
                padding: '1rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                border: hasSupport ? '1px solid #81c784' : '1px solid #a3d9b1',
              }}
            >
              <h4 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#16a085' }}>
                Alternative Source Search Results
              </h4>
              {/* Success banner when alternative source supports the claim */}
              {hasSupport && verifiedSource && (
                <div
                  style={{
                    background: '#c8e6c9',
                    padding: '0.75rem 1rem',
                    borderRadius: '4px',
                    marginBottom: '0.75rem',
                    border: '1px solid #66bb6a',
                  }}
                >
                  <strong style={{ color: '#2e7d32' }}>Alternative Source Found:</strong>{' '}
                  <span style={{ color: '#333' }}>
                    Claim supported by{' '}
                    <a
                      href={verifiedSource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1565c0', fontWeight: 500 }}
                    >
                      {verifiedSource.title}
                    </a>
                  </span>
                  {fact.validation_score != null && (
                    <span style={{ marginLeft: '0.5rem', color: '#555', fontSize: '0.85rem' }}>
                      ({(fact.validation_score * 100).toFixed(0)}% confidence)
                    </span>
                  )}
                </div>
              )}
              {fact.source_search_summary && (
                <p style={{ fontSize: '0.9rem', color: '#555', marginBottom: '0.75rem' }}>
                  {fact.source_search_summary}
                </p>
              )}
              {fact.source_search_performed_at && (
                <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginBottom: '0.5rem' }}>
                  Searched {new Date(fact.source_search_performed_at).toLocaleString()}
                </p>
              )}
              <strong style={{ fontSize: '0.9rem' }}>
                Candidate Sources ({fact.source_search_candidates.length}):
              </strong>
              {fact.source_search_candidates.map((source, idx) => (
                <div
                  key={idx}
                  style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    background: 'white',
                    borderRadius: '4px',
                    border: source.verified ? '2px solid #27ae60' : '1px solid #e0e0e0',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#3498db', fontWeight: 500, fontSize: '0.9rem' }}
                    >
                      {source.title}
                    </a>
                    {source.is_primary_source && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          padding: '1px 6px',
                          background: '#2ecc71',
                          color: 'white',
                          borderRadius: '3px',
                          fontWeight: 600,
                        }}
                      >
                        PRIMARY
                      </span>
                    )}
                    {source.verified && (
                      <span
                        style={{
                          fontSize: '0.7rem',
                          padding: '1px 6px',
                          background: '#27ae60',
                          color: 'white',
                          borderRadius: '3px',
                          fontWeight: 600,
                        }}
                      >
                        VERIFIED
                      </span>
                    )}
                    {source.published_date && (
                      <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                        {source.published_date}
                      </span>
                    )}
                  </div>
                  <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: '#555' }}>
                    {source.snippet}
                  </p>
                  {source.relevance_note && (
                    <p
                      style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.8rem',
                        color: '#7f8c8d',
                        fontStyle: 'italic',
                      }}
                    >
                      {source.relevance_note}
                    </p>
                  )}
                  {source.cited_primary_source_description && (
                    <p
                      style={{
                        margin: '0.25rem 0 0 0',
                        fontSize: '0.8rem',
                        color: '#8e44ad',
                      }}
                    >
                      Cites: {source.cited_primary_source_description}
                      {source.cited_primary_source_url && (
                        <>
                          {' '}
                          <a
                            href={source.cited_primary_source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#3498db' }}
                          >
                            (link)
                          </a>
                        </>
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )
        })()}

      {/* Alternative Source Searches - links to child verification records */}
      {fact.source_search_history &&
        fact.source_search_history.length > 0 &&
        (() => {
          const hasAltFound = fact.source_search_history.some(
            (e) => e.verification_classification === 'SUPPORTS'
          )
          return (
            <div
              style={{
                background: hasAltFound ? '#e8f5e9' : '#f5f5f5',
                padding: '1rem',
                borderRadius: '6px',
                marginBottom: '1rem',
                border: hasAltFound ? '1px solid #81c784' : '1px solid #e0e0e0',
              }}
            >
              <h4
                style={{
                  marginTop: 0,
                  marginBottom: '0.75rem',
                  color: hasAltFound ? '#2e7d32' : '#555',
                }}
              >
                {hasAltFound
                  ? 'Alternative Source Found'
                  : `Alternative Source Searches (${fact.source_search_history.length})`}
              </h4>
              {fact.source_search_history.map((entry, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background:
                      entry.verification_classification === 'SUPPORTS' ? '#c8e6c9' : 'white',
                    borderRadius: '4px',
                    marginBottom: '0.5rem',
                    border:
                      entry.verification_classification === 'SUPPORTS'
                        ? '1px solid #66bb6a'
                        : '1px solid #e9ecef',
                    fontSize: '0.85rem',
                  }}
                >
                  <div style={{ color: '#444' }}>
                    {entry.verification_classification && (
                      <>
                        <strong>
                          {entry.verification_classification === 'SUPPORTS'
                            ? 'Alternative source supports this claim'
                            : entry.verification_classification === 'REFUTES'
                              ? 'Alternative source did not support this claim'
                              : 'Inconclusive from alternative source'}
                        </strong>
                        {entry.verification_score != null && (
                          <span style={{ color: '#666', fontWeight: 'normal' }}>
                            {' '}
                            ({(entry.verification_score * 100).toFixed(0)}% confidence)
                          </span>
                        )}
                        {entry.verification_display_id && (
                          <>
                            {' '}
                            &mdash;{' '}
                            <a
                              href={`/results/${entry.verification_display_id}`}
                              style={{ color: '#3498db' }}
                            >
                              View full verification
                            </a>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div style={{ color: '#7f8c8d', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                    Searched {new Date(entry.searched_at).toLocaleString()} &middot;{' '}
                    {entry.sources_found} source(s) found
                    {entry.search_summary && <> &middot; {entry.search_summary}</>}
                  </div>
                </div>
              ))}
            </div>
          )
        })()}

      {/* Reasoning - always visible */}
      <CollapsibleSection title="Reasoning" defaultOpen={true}>
        <p style={{ margin: 0, color: '#555' }}>
          {fact.reasoning || fact.reasoning_preview || 'No reasoning provided'}
        </p>
      </CollapsibleSection>

      {/* Evidence Found */}
      {fact.evidence_found && (
        <CollapsibleSection title="Evidence Found" defaultOpen={true}>
          <p style={{ margin: 0, color: '#555' }}>{fact.evidence_found}</p>
        </CollapsibleSection>
      )}

      {/* Source Quotes - collapsed by default since can be verbose */}
      {fact.source_quotes && fact.source_quotes.length > 0 && (
        <CollapsibleSection title={`Source Quotes (${fact.source_quotes.length})`}>
          {fact.source_quotes.map((quote, idx) => (
            <blockquote
              key={idx}
              style={{
                borderLeft: '3px solid #3498db',
                paddingLeft: '1rem',
                marginLeft: 0,
                marginBottom: '0.5rem',
                color: '#555',
                fontStyle: 'italic',
              }}
            >
              &ldquo;{quote}&rdquo;
            </blockquote>
          ))}
        </CollapsibleSection>
      )}

      {/* Source Credibility - collapsible */}
      {(fact.source_type || fact.source_credibility_score) && (
        <CollapsibleSection
          title={`Source Credibility${fact.source_credibility_score ? ` (${fact.source_credibility_score}/10)` : ''}`}
        >
          <div
            style={{
              background: '#f8f9fa',
              padding: '1rem',
              borderRadius: '4px',
              border: '1px solid #e9ecef',
            }}
          >
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
              {fact.source_type && (
                <div>
                  <strong>Type:</strong> {fact.source_type}
                </div>
              )}
              {fact.source_credibility_score && (
                <div>
                  <strong>Score:</strong>{' '}
                  <span
                    style={{
                      color:
                        fact.source_credibility_score >= 7
                          ? '#27ae60'
                          : fact.source_credibility_score >= 4
                            ? '#f39c12'
                            : '#e74c3c',
                      fontWeight: 'bold',
                    }}
                  >
                    {fact.source_credibility_score}/10
                  </span>
                </div>
              )}
              {fact.is_primary_source !== undefined && (
                <div>
                  <strong>Source:</strong>{' '}
                  <span
                    style={{
                      color: fact.is_primary_source ? '#27ae60' : '#f39c12',
                      fontWeight: 'bold',
                    }}
                  >
                    {fact.is_primary_source ? 'Primary' : 'Secondary'}
                  </span>
                </div>
              )}
            </div>
            {fact.source_credibility_justification && (
              <p
                style={{
                  marginTop: '0.5rem',
                  marginBottom: 0,
                  color: '#6c757d',
                  fontSize: '0.9rem',
                }}
              >
                {fact.source_credibility_justification}
              </p>
            )}
            {/* Primary Source Reference */}
            {!fact.is_primary_source &&
              (fact.cited_primary_source_url || fact.cited_primary_source_description) && (
                <div
                  style={{
                    marginTop: '0.75rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid #dee2e6',
                  }}
                >
                  <strong style={{ fontSize: '0.9rem' }}>📚 Primary Source:</strong>
                  {fact.cited_primary_source_description && (
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem', color: '#555' }}>
                      {fact.cited_primary_source_description}
                    </p>
                  )}
                  {fact.cited_primary_source_url && (
                    <a
                      href={fact.cited_primary_source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'block',
                        marginTop: '0.25rem',
                        color: '#3498db',
                        fontSize: '0.85rem',
                        wordBreak: 'break-all',
                      }}
                    >
                      {fact.cited_primary_source_url}
                    </a>
                  )}
                </div>
              )}
          </div>
        </CollapsibleSection>
      )}

      {/* Human Validation Actions - show for new validation or editing */}
      {userEmail && (!hasHumanValidation || isEditing) && (
        <div style={{ marginTop: '1rem' }}>
          {!showValidation && !isEditing ? (
            <button
              onClick={() => setShowValidation(true)}
              style={{
                background: '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Add Human Validation
            </button>
          ) : (
            <div style={{ marginTop: '1rem' }}>
              {isEditing && (
                <h4 style={{ marginTop: 0, marginBottom: '0.5rem' }}>Edit Validation</h4>
              )}
              <textarea
                placeholder="Optional notes..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  padding: '0.5rem',
                  marginBottom: '0.5rem',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handleValidate(true)}
                  disabled={isValidating}
                  style={{
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {isValidating ? 'Saving...' : isEditing ? 'Save as Accurate' : 'Mark as Accurate'}
                </button>
                <button
                  onClick={() => handleValidate(false)}
                  disabled={isValidating}
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {isValidating
                    ? 'Saving...'
                    : isEditing
                      ? 'Save as Inaccurate'
                      : 'Mark as Inaccurate'}
                </button>
                <button
                  onClick={() => {
                    setShowValidation(false)
                    setIsEditing(false)
                    setNotes('')
                  }}
                  disabled={isValidating}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '0.5rem 1rem',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Provide Source Modal */}
      {showProvideSource && effectiveCitation && (
        <ProvideSourceModal
          sourceUrl={effectiveCitation}
          onClose={() => setShowProvideSource(false)}
          onSuccess={() => {
            setShowProvideSource(false)
            onSourceProvided?.() // This will refetch the result with updated cached_source_status
          }}
        />
      )}

      {/* AI Disclaimer */}
      <div
        style={{
          marginTop: '1rem',
          paddingTop: '0.75rem',
          borderTop: '1px solid #eee',
          fontSize: '0.75rem',
          color: '#7f8c8d',
          lineHeight: 1.4,
        }}
      >
        AI-generated assessment. Use human validation to confirm or correct.
      </div>
    </div>
  )
}
